import { useRoute, useRouter } from 'vue-router';
import { computed, Ref, ref, watch } from 'vue';
import type { ComputedRef } from 'vue';
import { useAuthStore } from '@/common/stores/auth';
import { isEmpty, lowerCase, upperCase } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useViewManagerStore } from '@/common/stores/view-manager';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { useRolePermission } from '@/common/permission/permission.utils';
import { getUtcOffset } from '@/common/utils/commonUtils';
import { ONE_HOUR, ONE_SECOND } from '@/common/utils';
import { LicenseOnSiteAlertItem, OnSiteAlertItem } from '@/api-client/alert/model';
import { useInternational } from '@/common/locale';
import { getDisplayLicenseTargetName } from '@/config/views/management/license/license.util';
import { useQuery } from '@tanstack/vue-query';
import {
  getV6OnSiteAlertLicenseNotification,
  getV6OnSiteAlertNotification,
} from '@/api-client/alert/queries/alert-common-controller/alert-common-controller';
import { getCurrentUtcPeriod } from '@/worker/utils';
import { useMyInfoStore } from '@/config/stores/my-info';

type Alarm = {
  type: 'alert' | 'license';
  data: {
    id: string;
    title: string;
    content: string;
    level?: 'critical' | 'warning';
    count: number;
  };
  isShow: boolean;
  isTriggered: boolean;
  theme: 'critical' | 'warning';
  cursor: 'pointer' | 'wait' | 'not-allowed';
  onClose: (event: MouseEvent) => Promise<void>;
  onMoveToView: (event: MouseEvent) => Promise<void>;
};

const HIDE_ALARM_URLS = ['/', '/popup'] as const;

export const ON_SITE_ALERT_FROM = 'onSiteAlert';
export const ON_SITE_LICENSE_FROM = 'onSiteLicense';

interface OnSiteAlertParams {
  userId: number;
  utcOffset: number;
}

interface OnSiteLicenseParams {
  utcOffset: number;
}

const useOnsiteQuery = <Data>({
  enabled,
  initialData,
  queryKey,
  queryFn,
  refetchInterval,
}: {
  enabled: ComputedRef<boolean>;
  initialData: Data;
  queryKey: ComputedRef<readonly unknown[]>;
  queryFn: (context: { signal: AbortSignal }) => Promise<Data>;
  refetchInterval: number;
}) => {
  const data = ref<Data>(initialData);
  const query = useQuery({
    queryKey,
    enabled,
    refetchInterval,
    queryFn,
  });

  const initData = () => {
    data.value = initialData;
  };

  watch(
    () => query.data.value,
    (value) => {
      data.value = (value ?? initialData) as Data;
    },
  );

  watch(
    () => query.error.value,
    (curError) => {
      if (!isEmpty(curError)) {
        initData();
      }
    },
  );

  return {
    data,
    initData,
  };
};

const useAlertApi = () => {
  const { myInfo } = storeToRefs(useMyInfoStore());
  const accessToken = computed(() => useAuthStore().getToken('accessToken'));
  const onSiteEnabled = computed(() => myInfo.value?.onSiteEnabled === true);
  const route = useRoute();
  const params = computed<OnSiteAlertParams>(() => ({
    utcOffset: getUtcOffset(),
    userId: myInfo.value?.userId ?? 0,
  }));
  const enabled = computed(() => {
    if (HIDE_ALARM_URLS.includes(route.path as (typeof HIDE_ALARM_URLS)[number])) {
      return false;
    }
    return !!accessToken.value && onSiteEnabled.value && params.value.userId > 0;
  });
  const queryKey = computed(() => ['onSiteAlert', params.value] as const);

  const { data, initData } = useOnsiteQuery<OnSiteAlertItem>({
    enabled,
    initialData: {} as OnSiteAlertItem,
    queryKey,
    refetchInterval: ONE_SECOND * 5,
    queryFn: async ({ signal }) => {
      const { fromTime, toTime } = getCurrentUtcPeriod({
        utcOffset: params.value.utcOffset,
        timeOffset: 5,
        timeOffsetUnit: 's',
      });
      const response = await getV6OnSiteAlertNotification(
        {
          fromTime,
          toTime,
          userId: params.value.userId,
        },
        undefined,
        signal,
      );
      return response.data?.data?.[0] ?? {};
    },
  });

  return {
    data,
    initData,
  };
};

const useLicenseApi = () => {
  const accessToken = computed(() => useAuthStore().getToken('accessToken'));
  const route = useRoute();
  const params = computed<OnSiteLicenseParams>(() => ({
    utcOffset: getUtcOffset(),
  }));
  const enabled = computed(() => {
    if (HIDE_ALARM_URLS.includes(route.path as (typeof HIDE_ALARM_URLS)[number])) {
      return false;
    }
    return !!accessToken.value;
  });
  const queryKey = computed(() => ['onSiteLicense', params.value] as const);

  const { data, initData } = useOnsiteQuery<LicenseOnSiteAlertItem>({
    enabled,
    initialData: {} as LicenseOnSiteAlertItem,
    queryKey,
    refetchInterval: ONE_HOUR,
    queryFn: async ({ signal }) => {
      const { fromTime, toTime } = getCurrentUtcPeriod({
        utcOffset: params.value.utcOffset,
        timeOffset: 1,
        timeOffsetUnit: 'h',
      });
      const response = await getV6OnSiteAlertLicenseNotification(
        {
          fromTime,
          toTime,
        },
        undefined,
        signal,
      );
      return response.data?.data?.[0] ?? {};
    },
  });

  return {
    data,
    initData,
  };
};

type UseApi = () => {
  data: Ref<any>;
  initData: () => void;
};

const useOnsite = <T extends UseApi>({ useApi }: { useApi: T }) => {
  const route = useRoute();

  const { data, initData } = useApi();

  const { isLoadingRouterForEach: isLoadingView } = storeToRefs(useViewManagerStore());

  const isShow = ref(false);
  const isTriggered = ref(false);
  const cursor = ref<Alarm['cursor']>('pointer');

  const preservedData = ref({ ...data.value });
  watch(data, (curData) => {
    if (!isEmpty(curData)) {
      preservedData.value = curData;
    }
  });

  watch(
    [preservedData, isLoadingView, () => route.path],
    ([curPreservedData, curIsLoadingView, curPath]) => {
      const isShowAvail =
        !HIDE_ALARM_URLS.includes(curPath as (typeof HIDE_ALARM_URLS)[number]) &&
        !curIsLoadingView &&
        !isEmpty(curPreservedData);
      if (isShowAvail) {
        cursor.value = 'pointer';
        isShow.value = true;
        setTimeout(() => {
          isTriggered.value = true;
        }, 500);
        return;
      }

      isShow.value = false;
      setTimeout(() => {
        isTriggered.value = false;
      }, 500);
    },
  );

  const onClosePreserved = async (event: MouseEvent) => {
    const element = (event.target as HTMLElement | null)?.closest('.on-site-alarm');
    if (!element) {
      return;
    }
    element.classList.add('hide');

    await new Promise((resolve) => setTimeout(resolve, 500));

    element.classList.remove('hide');
    preservedData.value = {};
    initData();
  };

  return {
    isShow,
    isTriggered,
    cursor,
    preservedData,
    onClosePreserved,
  };
};

const useAlert = () => {
  const { isShow, isTriggered, cursor, preservedData, onClosePreserved } = useOnsite<
    typeof useAlertApi
  >({
    useApi: useAlertApi,
  });

  const router = useRouter();
  const { isPermissionDenied } = useRolePermission();

  const onMoveToAlertView = async (event: MouseEvent) => {
    const { ruleId, ruleName, ruleType } = preservedData.value;
    if (!ruleId) {
      return;
    }

    cursor.value = 'wait';
    const hasViewPerm = !isPermissionDenied({
      rolePermissionKey: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALERT_LIST,
      type: 'menuOnOff',
    });
    if (!hasViewPerm) {
      cursor.value = 'not-allowed';
      return;
    }

    const queryParams = new URLSearchParams({
      from: ON_SITE_ALERT_FROM,
      ruleId,
      ruleName: ruleName || '',
      ruleType: ruleType || '',
    }).toString();

    await router.push(`/alert/list?${queryParams}`);

    await onClosePreserved(event);
  };

  const alertData = computed<Alarm>(() => {
    const level = lowerCase(preservedData.value.level) === 'critical' ? 'critical' : 'warning';
    return {
      type: 'alert',
      data: {
        ...preservedData.value,
        id: preservedData.value.pushId,
        level,
      },
      isShow: isShow.value,
      isTriggered: isTriggered.value,
      theme: level,
      cursor: cursor.value,
      onClose: onClosePreserved,
      onMoveToView: onMoveToAlertView,
    };
  });

  return {
    alertData,
  };
};

const useLicense = () => {
  const { isShow, isTriggered, cursor, preservedData, onClosePreserved } = useOnsite<
    typeof useLicenseApi
  >({
    useApi: useLicenseApi,
  });

  const router = useRouter();
  const { isPermissionDenied } = useRolePermission();

  const onMoveToLicenseView = async () => {
    const hasViewPerm = !isPermissionDenied({
      rolePermissionKey: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_LICENSE,
      type: 'menuOnOff',
    });
    if (!hasViewPerm) {
      cursor.value = 'not-allowed';
      return;
    }

    const { count, platform } = preservedData.value;
    const queryParams = new URLSearchParams({
      from: ON_SITE_LICENSE_FROM,
      platform: count > 0 ? 'overview' : lowerCase(platform),
    }).toString();

    await router.push(`/management/license?${queryParams}`);
  };

  const onMoveToInstanceView = async () => {
    const hasViewPerm = !isPermissionDenied({
      rolePermissionKey: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
      type: 'menuOnOff',
    });
    if (!hasViewPerm) {
      cursor.value = 'not-allowed';
      return;
    }

    const queryParams = new URLSearchParams({
      from: ON_SITE_LICENSE_FROM,
    }).toString();

    await router.push(`/database/instance?${queryParams}`);
  };

  const onMoveToView = async (event: MouseEvent) => {
    cursor.value = 'wait';

    const kind = preservedData.value.kind === 'check failed' ? 'check failed' : 'exceeded';
    if (kind === 'check failed') {
      await onMoveToInstanceView();
    } else {
      await onMoveToLicenseView();
    }

    await onClosePreserved(event);
  };

  const { t } = useInternational();

  const getLocaleByKind = ({ key, kind }: { key: string; kind: string }) => {
    const locale = {
      exceeded: {
        title: t('WORD.EXCEEDED'),
        content: t('MESSAGE.ISSUE_LICENSE'),
      },
      'check failed': {
        title: t('WORD.CHECK'),
        content: t('MESSAGE.CHECK_FAIL_LICENSE'),
      },
    };
    return locale[kind]?.[key] ?? '';
  };

  const licenseData = computed<Alarm>(() => {
    const kind = preservedData.value.kind === 'check failed' ? 'check failed' : 'exceeded';
    return {
      type: 'license',
      data: {
        ...preservedData.value,
        id: preservedData.value.pushId,
        title: `${t('WORD.LICENSE')} ${getLocaleByKind({
          key: 'title',
          kind,
        })}`,
        content: `${t('WORD.PLATFORM')} : ${t(
          `WORD.GNB.${upperCase(preservedData.value.platform)}`,
          preservedData.value.platform,
        )}\n${t('WORD.LICENSE')} ${t('WORD.TARGET')} : ${getDisplayLicenseTargetName(
          preservedData.value.licenseType,
        )}\n\n${getLocaleByKind({
          key: 'content',
          kind,
        })}`,
      },
      isShow: isShow.value,
      isTriggered: isTriggered.value,
      theme: 'critical',
      cursor: cursor.value,
      onClose: onClosePreserved,
      onMoveToView,
    };
  });

  return {
    licenseData,
  };
};

export const setup = () => {
  const { alertData } = useAlert();
  const { licenseData } = useLicense();

  const alarms = ref<Alarm[]>([]);

  watch(
    [alertData, licenseData],
    (alarmData) => {
      alarmData.forEach((data) => {
        const foundIdx = alarms.value.findIndex(({ type }) => type === data.type);
        if (data.isShow) {
          if (foundIdx === -1) {
            alarms.value.unshift(data);
          } else {
            alarms.value.splice(foundIdx, 1, data);
          }
          return;
        }

        if (foundIdx !== -1) {
          alarms.value.splice(foundIdx, 1);
        }
      });
    },
    { deep: true },
  );

  return {
    alarms,
  };
};
